import React, { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Box, Typography, Grid, IconButton, InputBase, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery, useMutation } from "@tanstack/react-query";
import apiClient from "lib/api-client";
import createApiClient from "lib/create-api-client";
import { toast } from "react-toastify";
import { ReactComponent as MicIcon } from "assets/icons/Mic.svg";
import CalenderComp from "features/Setting/order/components/CalenderComp";
import dayjs from "dayjs";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import Selectinput from "features/order_client_details/components/Selectinput";
import CommentComp from "components/ui/commentInput/CommentComp";
import AudioPlayer from "components/ui/commentSection/audioPlayer/AudioPlayer";
import { getUserDetail } from "utils/getUserDetailsHelper";
import "./createprojectmodal.css";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "95vw", md: "90vw", lg: "1281px" },
    maxWidth: "1281px",
    maxHeight: "92vh",
    height: "auto",
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },

    bgcolor: "#101010",
    border: "1px solid #1c1c1c",
    outline: "none",
    boxShadow: 24,
    padding: { xs: "20px", md: "30px 40px" },
    borderRadius: "10px",
    color: "#fff",
    boxSizing: "border-box",
};

const inputStyle = {
    width: "100%",
    borderRadius: "6px",
    color: "#fff",
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "50px",
    background: "#000",
    border: "1px solid #fff",
};

const CreateModificationModal = ({ open, handleClose, designData }) => {
    const [designNo, setDesignNo] = useState("");
    const [modType, setModType] = useState("");
    const [cadPerson, setCadPerson] = useState("");
    const [dueDate, setDueDate] = useState(dayjs());
    const [comments, setComments] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);

    const [startRecording, setStartRecording] = useState(false);
    const commentRef = useRef(null);
    const [recordedData, setRecordedData] = useState(false);
    const [recordedBlobData, setRecordedBlobData] = useState(null);
    const [showAudioPlayer, setAudioPlayer] = useState(false);

    const resetFormState = () => {
        setDesignNo(designData?.styleNumber || "");
        setModType("");
        setCadPerson("");
        setDueDate(dayjs());
        setComments("");
        setAnchorEl(null);
        setStartRecording(false);
        setRecordedData(false);
        setRecordedBlobData(null);
        setAudioPlayer(false);
    };

    const { data: cadPersonRaw = [], isLoading: cadPersonLoading } = useQuery({
        queryKey: ["createProjectUtils", "getCadPersonDetails"],
        queryFn: async () => {
            const res = await apiClient.get("/api/v10/createProjectUtils/getCadPersonDetails");
            if (!res.data?.success) return [];
            return Array.isArray(res.data.data) ? res.data.data : [];
        },
        enabled: open,
        staleTime: 5 * 60 * 1000,
    });

    const cadPersonOptions = useMemo(() => cadPersonRaw.map((u) => u.cadUserName).filter(Boolean), [cadPersonRaw]);

    const cadPersonIdByName = useMemo(() => {
        const map = new Map();
        cadPersonRaw.forEach((u) => {
            const name = u?.cadUserName;
            if (!name) return;
            const id = u?._id ?? u?.id ?? u?.cadUserId;
            if (id) map.set(name, String(id));
        });
        return map;
    }, [cadPersonRaw]);

    useEffect(() => {
        if (designData?.styleNumber) {
            setDesignNo(designData.styleNumber);
        }
    }, [designData]);

    const handleModalClose = () => {
        resetFormState();
        handleClose();
    };

    const onCompleted = () => {
        handleModalClose();
    };

    const { mutate: submitModification, isPending: isSubmitting } = useMutation({
        mutationFn: async (formData) => {
            console.log("FormData payload:");
            for (const [key, value] of formData.entries()) {
                if (value instanceof File) {
                    console.log(`${key}:`, { name: value.name, type: value.type, size: value.size });
                } else {
                    console.log(`${key}:`, value);
                }
            }
            try {
                const res = await createApiClient.post("/api/v1/other/createMod", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
                return res.data;
            } catch (error) {
                if (error?.response != null || error?.request != null) {
                    throw error;
                }
                const message = error instanceof Error ? error.message : typeof error === "string" ? error : "Something went wrong";
                throw new Error(message);
            }
        },

        onSuccess: (data) => {
            const ok = data?.status === 200 || data?.success === true;
            if (ok) {
                toast.success(data?.message || "Modification created successfully");
                onCompleted();
            } else {
                toast.error(data?.message || data?.error || "Request failed");
            }
        },
        onError: (err) => {
            const msg = err?.response?.data?.message || err?.message || "Something went wrong";
            toast.error(msg);
        },
    });

    const resolveVoiceFileForForm = async () => {
        if (recordedBlobData instanceof File) {
            return recordedBlobData;
        }
        if (typeof recordedData === "string" && recordedData.startsWith("blob:")) {
            const r = await fetch(recordedData);
            const blob = await r.blob();
            return new File([blob], "recording.webm", { type: blob.type || "audio/webm" });
        }
        return null;
    };

    /** Multipart `createMod`: text fields + `imageUrls` JSON string + binary `file` when a recording exists. */
    const buildCreateModificationFormData = async () => {
        const urls = [designData?.Orgimg, designData?.imageUrl].filter((u) => typeof u === "string" && u.trim() !== "");
        const bodyImageUrls = [...new Set(urls)];
        const trimmedComment = comments.trim();

        const cadPersonValue = cadPerson ? cadPersonIdByName.get(cadPerson) || (/^[a-f\d]{24}$/i.test(cadPerson) ? cadPerson : "") : "";

        const userId = getUserDetail("_id");

        const category = designData?.category || designData?.categoryName || "";

        const formData = new FormData();
        formData.append("designNo", designNo.trim());
        formData.append("modificationType", modType);
        formData.append("category", category);
        formData.append("dueDate", dayjs(dueDate).isValid() ? dayjs(dueDate).toISOString() : "");
        formData.append("description", trimmedComment);
        formData.append("imageUrls", JSON.stringify(bodyImageUrls));

        if (cadPersonValue) {
            formData.append("cadPerson", cadPersonValue);
        }
        if (userId != null && String(userId) !== "") {
            formData.append("userId", String(userId));
        }
        const voiceFile = await resolveVoiceFileForForm();
        if (voiceFile && voiceFile.size > 0) {
            formData.append("file", voiceFile, voiceFile.name);
        }
        return formData;
    };

    const handleProceed = async () => {
        if (!isFormValid || isSubmitting) return;
        try {
            submitModification(await buildCreateModificationFormData());
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || "Failed to submit modification";
            toast.error(msg);
        }
    };

    useEffect(() => {
        return () => {
            if (typeof recordedData === "string" && recordedData.startsWith("blob:")) {
                URL.revokeObjectURL(recordedData);
            }
        };
    }, [recordedData]);

    useEffect(() => {
        if (!open) {
            resetFormState();
        }
    }, [open]);

    const handleMicClick = () => {
        setStartRecording(true);
        setTimeout(() => {
            commentRef.current?.handleMicFunction();
        }, 100);
    };

    const handleDateSelect = (formattedDate) => {
        setDueDate(formattedDate);
        setAnchorEl(null);
    };

    const hasCommentOrVoice = Boolean(
        comments.trim() ||
        (typeof recordedData === "string" && recordedData.trim() && recordedData !== "false") ||
        recordedBlobData instanceof File
    );
    const isFormValid = Boolean(designNo && modType && dueDate && hasCommentOrVoice);

    return (
        <>
            <Modal open={open} onClose={handleModalClose}>
                <Box sx={style}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, borderBottom: "2px solid #1c1c1c", pb: 2 }}>
                        <Typography variant="h6" sx={{ fontFamily: "Poppins", fontSize: "22px", fontWeight: 400, marginBottom: "12px" }}>
                            Create Modification
                        </Typography>
                        <IconButton onClick={handleModalClose} sx={{ color: "#888" }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Grid container spacing={3}>
                        <Grid item xs={7}>
                            <Grid container columnSpacing="40px" rowSpacing="30px">
                                <Grid item xs={12} sm={6}>
                                    <Typography sx={{ fontWeight: 400, lineHeight: "22px", fontSize: "18px", color: "#ccc", mb: 1 }}>Design No.</Typography>
                                    <Box sx={inputStyle}>
                                        <InputBase value={designNo} onChange={(e) => setDesignNo(e.target.value)} sx={{ color: "#fff", flex: 1, fontSize: "13px" }} />
                                        {designNo && (
                                            <IconButton size="small" onClick={() => setDesignNo("")} sx={{ color: "#888" }}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography sx={{ fontWeight: 400, lineHeight: "22px", fontSize: "18px", color: "#ccc", mb: 1 }}>Modification Type</Typography>
                                    <Selectinput
                                        options={[
                                            "Reduce Gold Weight",
                                            "Reduce Diamond Pieces",
                                            "Reduce Height",
                                            "Other Modification",
                                        ]}
                                        value={modType}
                                        onchange={(e) => setModType(e.target.value)}
                                        placeHolder={"Choose Modification Type"}
                                        menuItemStyle={{ justifyContent: "center !important", textAlign: "center !important" }}
                                        customStyle={{
                                            fontFamily: "Poppins",
                                            fontWeight: 500,
                                            fontSize: "16px",
                                            width: "100%",
                                            height: "50px",
                                            justifyContent: "center",
                                            background: "#000",
                                            color: "#fff",
                                            border: "1px solid #333",
                                            borderRadius: "4px",
                                        }}
                                        customPlaceHolderStyle={{ fontFamily: "Poppins", fontWeight: 500, fontSize: "16px", color: "#888" }}
                                        customSharedStyle={{ fontFamily: "Poppins", fontWeight: 500, fontSize: "16px", color: "#fff" }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography sx={{ fontWeight: 400, lineHeight: "22px", fontSize: "18px", color: "#ccc", mb: 1 }}>
                                        CAD Person{" "}
                                        <Typography component="span" sx={{ fontSize: "12px", color: "#666" }}>
                                            (Optional)
                                        </Typography>
                                    </Typography>
                                    <Selectinput
                                        options={cadPersonLoading ? [] : cadPersonOptions}
                                        value={cadPerson}
                                        onchange={(e) => setCadPerson(e.target.value)}
                                        placeHolder={cadPersonLoading ? "Loading…" : "Choose Name"}
                                        customStyle={{
                                            fontFamily: "Poppins",
                                            fontWeight: 500,
                                            fontSize: "16px",
                                            width: "100%",
                                            height: "50px",
                                            background: "#000",
                                            color: "#fff",
                                            border: "1px solid #333",
                                            borderRadius: "4px",
                                        }}
                                        customPlaceHolderStyle={{ fontFamily: "Poppins", fontWeight: 500, fontSize: "16px", color: "#888" }}
                                        customSharedStyle={{ fontFamily: "Poppins", fontWeight: 500, fontSize: "16px", color: "#fff" }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography sx={{ fontWeight: 400, lineHeight: "22px", fontSize: "18px", color: "#ccc", mb: 1 }}>Due Date</Typography>
                                    <Box sx={{ ...inputStyle, cursor: "pointer" }} onClick={(e) => setAnchorEl(e.currentTarget)}>
                                        <Typography sx={{ fontFamily: "Poppins", fontWeight: 500, fontSize: "16px", color: dueDate ? "#fff" : "#888" }}>
                                            {dueDate ? dayjs(dueDate).format("DD MMM YYYY") : "Choose Date"}
                                        </Typography>
                                        <CalendarTodayOutlinedIcon sx={{ color: "#888", fontSize: "18px" }} />
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <div style={{ position: "relative", marginBottom: "8px", display: "flex", alignItems: "center" }}>
                                        <Typography sx={{ fontWeight: 400, lineHeight: "22px", fontSize: "18px", color: "#ccc", margin: 0 }}>Comments</Typography>
                                    </div>
                                    <div style={{ position: "relative", marginTop: 20 }}>
                                        <textarea
                                            placeholder="Enter Message"
                                            value={comments}
                                            onChange={(e) => setComments(e.target.value)}
                                            style={{
                                                width: "100%",
                                                height: "140px",
                                                background: "#000",
                                                color: "#fff",
                                                borderRadius: 8,
                                                padding: 10,
                                                resize: "none",
                                                outline: "none",
                                                fontSize: "16px",
                                                fontFamily: "Poppins",
                                                fontWeight: 500,
                                            }}
                                        />
                                        <IconButton
                                            style={{
                                                position: "absolute",
                                                height: "35px",
                                                width: "35px",
                                                borderRadius: "50%",
                                                bottom: "8px",
                                                right: "-51px",
                                                color: "#fff",
                                                background: "#444444",
                                            }}
                                            onClick={handleMicClick}
                                        >
                                            <MicIcon />
                                        </IconButton>
                                        {startRecording && (
                                            <CommentComp
                                                ref={commentRef}
                                                isFileUpload={false}
                                                isRecordOnly={true}
                                                hidePlaceholder={true}
                                                skipVoiceS3Upload
                                                initialComment={comments}
                                                onCommentTextChange={setComments}
                                                setStartRecording={setStartRecording}
                                                setRecordedData={setRecordedData}
                                                setRecordedBlobData={setRecordedBlobData}
                                                setAudioPlayer={setAudioPlayer}
                                                mode="audio"
                                            />
                                        )}
                                        {showAudioPlayer && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <AudioPlayer src={recordedData} />
                                                <IconButton
                                                    onClick={() => {
                                                        setRecordedData(false);
                                                        setRecordedBlobData(null);
                                                        setAudioPlayer(false);
                                                    }}
                                                    sx={{ color: "#888", bgcolor: "#1f1f1f", "&:hover": { bgcolor: "#2d2d2d" } }}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </div>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={5} sx={{ display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
                            <Box
                                sx={{
                                    width: "100%",
                                    maxWidth: "400px",
                                    aspectRatio: "1 / 1",
                                    bgcolor: "#fff",
                                    borderRadius: "5px",
                                    p: 1,
                                    boxSizing: "border-box",
                                    overflow: "hidden",
                                    display: "flex",
                                    alignItems: "stretch",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={designData?.Orgimg || designData?.imageUrl || "default-img.jpg"}
                                    alt="Design"
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        objectPosition: "center",
                                        borderRadius: "4px",
                                        display: "block",
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    <Box sx={{ display: "flex", justifyContent: "center", width: "100%", mt: 2 }}>
                        <Button
                            variant="contained"
                            disabled={!isFormValid || isSubmitting}
                            sx={{
                                width: "250px",
                                height: "63px",
                                fontSize: "18px",
                                fontWeight: 500,
                                fontFamily: "Poppins",
                                color: "#000",
                                bgcolor: isFormValid && !isSubmitting ? "#50C878" : "#444",
                                textTransform: "none",
                                px: 6,
                                py: 1,
                                borderRadius: "6px",
                                "&:hover": { bgcolor: isFormValid && !isSubmitting ? "#45b369" : "#444" },
                            }}
                            onClick={handleProceed}
                        >
                            {isSubmitting ? "Submitting…" : "Proceed"}
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <CalenderComp
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                isPopover={true}
                handleClose={() => setAnchorEl(null)}
                onDateSelect={handleDateSelect}
                disableRestrictedDates={true}
                isCreateProject={true}
            />
        </>
    );
};

export default CreateModificationModal;
